import { useEffect, useRef, useState } from "react";

import { APIError, getDeviceInstallation, type DeviceInstallation } from "./api";
import { displayLocale } from "./localization";

const installationSteps = [
  ["bootstrap", "Installer laden"],
  ["preflight", "System prüfen"],
  ["input", "CSV-Ordner und Dienst prüfen"],
  ["dependencies", "Systempakete installieren"],
  ["release", "Signiertes Agent-Release prüfen"],
  ["permissions", "Benutzer und Dateirechte einrichten"],
  ["certificate", "Gerätezertifikat einrichten"],
  ["configuration", "Agent konfigurieren"],
  ["services", "Dienste starten"],
  ["verification", "Installation prüfen"],
  ["complete", "Abgeschlossen"],
] as const;

const stepLabels = Object.fromEntries(installationSteps) as Record<string, string>;

function installationStateLabel(installation?: DeviceInstallation | null) {
  if (!installation) return "Noch nicht installiert";
  if (installation.state === "succeeded") return "Erfolgreich";
  if (installation.state === "failed") return "Fehlgeschlagen";
  if (installation.state === "prepared") return "Wartet auf Pi";
  return "Läuft";
}

function installationErrorParts(summary?: string) {
  const value = summary ?? "Unbekannter Installationsfehler";
  const delimiter = " Technische Diagnose: ";
  const delimiterIndex = value.indexOf(delimiter);
  if (delimiterIndex < 0) return { message: value, diagnostic: "" };
  return {
    message: value.slice(0, delimiterIndex),
    diagnostic: value.slice(delimiterIndex + delimiter.length),
  };
}

export function InstallationProgress({
  installationID,
  initialInstallation = null,
  preparedExpiresAt,
  onChange,
}: {
  installationID: string;
  initialInstallation?: DeviceInstallation | null;
  preparedExpiresAt?: string;
  onChange?: (installation: DeviceInstallation) => void;
}) {
  const [installation, setInstallation] = useState<DeviceInstallation | null>(initialInstallation);
  const [fetchError, setFetchError] = useState(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    async function refresh() {
      try {
        const current = await getDeviceInstallation(installationID);
        if (!active) return;
        setInstallation(current);
        setFetchError(false);
        onChangeRef.current?.(current);
        const deadline = current.state === "prepared" && preparedExpiresAt ? preparedExpiresAt : current.report_expires_at;
        if (current.state !== "succeeded" && current.state !== "failed" && new Date(deadline).getTime() > Date.now()) {
          timer = window.setTimeout(() => void refresh(), 2000);
        }
      } catch (reason) {
        if (!active) return;
        if (!(reason instanceof APIError) || reason.status >= 500) {
          setFetchError(true);
          timer = window.setTimeout(() => void refresh(), 5000);
        }
      }
    }

    void refresh();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [installationID, preparedExpiresAt]);

  const currentIndex = installationSteps.findIndex(([step]) => step === installation?.current_step);
  const terminalSuccess = installation?.state === "succeeded";
  const progressValue = terminalSuccess ? installationSteps.length : Math.max(0, currentIndex + 1);
  const currentLabel = installation ? (stepLabels[installation.current_step] ?? installation.current_step) : "Status wird geladen";
  const installationError = installationErrorParts(installation?.error_summary);

  return (
    <div className={`installation-status ${installation?.state ?? "prepared"}`} role="status" aria-live="polite">
      <div className="installation-status-heading">
        <div>
          <strong>{installationStateLabel(installation)}</strong>
          <span>{installation?.state === "prepared" ? "Der Installationsbefehl wurde noch nicht auf dem Raspberry Pi gestartet." : currentLabel}</span>
        </div>
        <b>{installation?.state === "succeeded" ? "Fertig" : installation?.state === "failed" ? `Stopp bei ${Math.max(1, currentIndex + 1)}/${installationSteps.length}` : `${progressValue}/${installationSteps.length}`}</b>
      </div>
      <progress aria-label="Installationsfortschritt" max={installationSteps.length} value={progressValue} />
      <ol className="installation-steps" aria-label="Installationsschritte">
        {installationSteps.map(([step, label], index) => {
          const done = terminalSuccess || index < currentIndex;
          const activeStep = index === currentIndex && !terminalSuccess;
          const failed = activeStep && installation?.state === "failed";
          return <li key={step} className={failed ? "failed" : activeStep ? "active" : done ? "done" : "pending"}><i aria-hidden="true" /><span>{label}</span></li>;
        })}
      </ol>
      {installation?.state === "failed" && <div className="installation-error"><strong>{installationError.message}</strong>{installationError.diagnostic && <span className="installation-diagnostic"><b>Technische Diagnose</b><code>{installationError.diagnostic}</code></span>}{installation.error_code && <span>Fehlercode: {installation.error_code}{installation.exit_code !== undefined ? ` · Exit ${installation.exit_code}` : ""}</span>}</div>}
      {installation?.state === "succeeded" && <p className="installation-result">Pi-Agent {installation.agent_version ?? "installiert"} wurde gestartet.</p>}
      {installation?.started_at && <small>Gestartet: {new Date(installation.started_at).toLocaleString(displayLocale)} · {installation.event_count} Statusmeldungen</small>}
      {fetchError && <small>Der Live-Status konnte kurzzeitig nicht aktualisiert werden. Neuer Versuch läuft automatisch.</small>}
    </div>
  );
}
